import { useCallback, useState, useMemo } from 'react';
import styled from '@emotion/styled';
import { TextField } from '@mui/material';
import type { CreateFolder } from '@trinity/chat/api/interfaces/workspaces';
import { ButtonSet } from '@trinity/chat-components/components/Modal/ButtonSet.js';
import { Header } from '@trinity/chat-components/components/Modal/Header.js';
import { useModal } from '@trinity/chat-components/components/Modal/hooks/index.js';
import { Modal } from '@trinity/chat-components/components/Modal/Modal.js';
import { Button } from '@trinity/chat-components/components/presentation/Button/index.js';
import { useCreateFolder } from '@/containers/Workspace.Folder/hooks/useCreateFolder';
import type { WorkspaceObject } from '@/types';
import type { UseModalProps } from '@trinity/chat-components/components/Modal/index.js';

type CreateFolderInsideParentModalProps = UseModalProps & {
  parentObject: Pick<WorkspaceObject, 'id' | 'workspaceId'>;
  onSuccess?: (res: CreateFolder.Response) => void;
};

// eslint-disable-next-line react-refresh/only-export-components
const CreateFolderInsideParentModal = ({
  onClose,
  onSuccess,
  open,
  parentObject,
}: CreateFolderInsideParentModalProps) => {
  const [name, setName] = useState<string>('');

  const { isPending, mutateAsync } = useCreateFolder({
    onSuccess: res => {
      onSuccess?.(res);
      onClose();
    },
  });

  const canSave = useMemo(() => {
    return !!name || isPending;
  }, [name, isPending]);

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  }, []);

  const handleSave = useCallback(() => {
    return mutateAsync({
      name,
      parentObjectId: parentObject.id,
      workspaceId: parentObject.workspaceId,
    });
  }, [mutateAsync, name, parentObject.id, parentObject.workspaceId]);

  return (
    <Modal onClose={onClose} open={open}>
      <Header
        text="New Folder" />
      <div>
        <Title>Title</Title>
        <div>
          <TextField
            sx={{ marginBottom: 4 }}
            fullWidth
            autoFocus
            variant='standard'
            name="folder-name"
            onChange={handleNameChange}
            value={name} />
        </div>
        {/* <div className={styles.label}>Location</div>
        {!!parentObject && location} */}
      </div>
      <ButtonSet>
        <Button.Destructive
          onClick={onClose}
          variant="brick">
          Cancel
        </Button.Destructive>
        <Button.Affirmative
          disabled={!canSave}
          onClick={handleSave}
          variant="brick">
          Create
        </Button.Affirmative>
      </ButtonSet>
    </Modal>
  );
};

const Title = styled('div')({
  //fontSize: '1.2rem',
  fontWeight: 'bold',
  marginBottom: '0.5rem',
});

export const useCreateFolderInsideParentModal = () => useModal(CreateFolderInsideParentModal);